import { Component, OnDestroy } from '@angular/core';
import { RischioAnaliticoStateService } from '../../../../../services/rischio-analitico-state.service';
import { HeatmapCella, Q95ClassCountRow } from '../../../../../models/rischio-analitico.model';

export interface RiepilogoTableData {
  point: string;
  r1: number;
  r2: number;
  r3: number;
  r4: number;
  r5: number;
  loq: number;
}

export const riskClassMap: Record<string, string> = {
  R1: 'r1',
  R2: 'r2',
  R3: 'r3',
  R4: 'r4',
  R5: 'r5',
  '< LOQ': 'loq'
};

@Component({
  selector: 'ebw-monorepo-rischio-analitico-riepilogo',
  templateUrl: './rischio-analitico-riepilogo.component.html',
  styleUrls: ['./rischio-analitico-riepilogo.component.scss']
})
export class RischioAnaliticoRiepilogoComponent implements OnDestroy {
  heatmap: HeatmapCella[] = [];

  displayedColumns: string[] = ['point', 'r5', 'r4', 'r3', 'r2', 'r1', 'loq'];
  dataSource: RiepilogoTableData[] = [];
  ordinePunti: string[] = [];

  get nessunDato(): boolean {
    return !this._rischioAnaliticoStateService.caricamentoRiepilogo$.value && !this.heatmap.length && !this.dataSource.length;
  }

  get messaggioNessunDato(): string {
    const annoRiferimento = this._rischioAnaliticoStateService.filtri.annoRiferimento;
    return `Nessun dato disponibile per l'anno di riferimento selezionato. Il 95° percentile considera i prelievi dei 3 anni solari precedenti: ${annoRiferimento - 3}–${annoRiferimento - 1} per il ${annoRiferimento}. Prova a selezionare un anno diverso nei filtri.`;
  }

  constructor(private _rischioAnaliticoStateService: RischioAnaliticoStateService) {
    // l'apertura del pannello fa partire il caricamento di heatmap e riepilogo (RischioAnaliticoComponent)
    this._rischioAnaliticoStateService.riepilogoAperto$.next(true);
    this.heatmap = this._rischioAnaliticoStateService.heatmap$.value;

    if (Array.isArray(this._rischioAnaliticoStateService.riepilogo$.value) && this._rischioAnaliticoStateService.riepilogo$.value.length > 0) {
      this._parseDataSource(this._rischioAnaliticoStateService.riepilogo$.value);
    }

    this._rischioAnaliticoStateService.heatmap$.subscribe((heatmap) => {
      this.heatmap = heatmap;
    });

    this._rischioAnaliticoStateService.riepilogo$.subscribe((riepilogo) => {
      if (!Array.isArray(riepilogo) || riepilogo.length === 0) {
        this.dataSource = [];
        this.ordinePunti = [];
        return;
      }

      this._parseDataSource(riepilogo);
    });
  }

  ngOnDestroy(): void {
    this._rischioAnaliticoStateService.riepilogoAperto$.next(false);
    this._rischioAnaliticoStateService.closeRiepilogo$.next();
  }

  private _parseDataSource(riepilogo: Q95ClassCountRow[]): void {
    const accumulatedRiepilogo: Record<string, Omit<RiepilogoTableData, 'point'>> = {};

    riepilogo.forEach((r) => {
      if (accumulatedRiepilogo[r.punto]) {
        accumulatedRiepilogo[r.punto][riskClassMap[r.risk_class]] = r.n;
      } else {
        accumulatedRiepilogo[r.punto] = {
          r1: r.risk_class === 'R1' ? r.n : 0,
          r2: r.risk_class === 'R2' ? r.n : 0,
          r3: r.risk_class === 'R3' ? r.n : 0,
          r4: r.risk_class === 'R4' ? r.n : 0,
          r5: r.risk_class === 'R5' ? r.n : 0,
          loq: r.risk_class === '< LOQ' ? r.n : 0
        };
      }
    });

    this.dataSource = Object.entries(accumulatedRiepilogo)
      .map(([key, value]) => ({ point: key, ...value }))
      .sort((a, b) => this._confrontaRighe(a, b));
    this.ordinePunti = this.dataSource.map((riga) => riga.point);
  }

  // righe in ordine decrescente di R5, poi R4, R3, R2, R1, < LOQ (stesso ordine delle colonne); a parita' per nome del punto
  private _confrontaRighe(a: RiepilogoTableData, b: RiepilogoTableData): number {
    const colonneClassi = this.displayedColumns.filter((colonna) => colonna !== 'point') as (keyof RiepilogoTableData)[];
    for (const colonna of colonneClassi) {
      const differenza = Number(b[colonna]) - Number(a[colonna]);
      if (differenza !== 0) return differenza;
    }
    return a.point.localeCompare(b.point);
  }
}
